import {
	ArrowLeftOutlined,
	DeleteOutlined,
	EditOutlined,
	FileSearchOutlined,
	LoadingOutlined,
} from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	Button,
	Checkbox,
	Col,
	DatePicker,
	Divider,
	Form,
	InputNumber,
	Row,
	Select,
	Space,
	Table,
	Typography,
} from 'antd';
import { FC, Fragment } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	finalizeGolive,
	getGoliveBrand,
	getGoliveDetail,
	GetGoliveDetailParam,
	SearchGoLiveParams,
	submitGolive,
	SubmitGolivePayload,
} from '@/libs/fleet-api/services/pipeline-golive';
import BrandModal, { BrandModalForm, useBrandModal } from './modals/BrandModal';
import BrandDetailModal, {
	useBrandDetailModal,
} from './modals/BrandDetailModal';
import { useForm } from '@/libs/antd/form';
import dayjs from 'dayjs';
import { formatCurrency } from '@/libs/antd/formater';
import rule from '@/libs/antd/rules';
import { useModals } from '@/components/modals/ModalProvider';
import { useApp } from '@/App';
import { useUserData } from '@/libs/fleet-api/hooks';
import { inputCurrency } from '@/libs/antd/input-number';

type GoliveForm = SubmitGolivePayload;

const GolivePage: FC<
	GetGoliveDetailParam & { backParams: SearchGoLiveParams }
> = ({ prospectId, prospectPlafonId, backParams }) => {
	const { groupName } = useUserData() ?? {};
	const { navigate, notification } = useApp();
	const { modal } = useModals();
	const { data, isFetching, refetch } = useQuery({
		queryKey: ['getGoliveDetail', prospectId, prospectPlafonId] as const,
		queryFn: ({ queryKey }) => {
			const [, prospectId] = queryKey;
			if (!prospectId || !prospectPlafonId) return null;
			return getGoliveDetail({
				prospectId,
				prospectPlafonId,
			});
		},
	});

	const getGoliveBrandMutation = useMutation({
		mutationFn: getGoliveBrand,
	});

	const brandModal = useBrandModal();
	const brandDetailModal = useBrandDetailModal();

	const {
		assetTypes,
		assetTypeOptions,
		progressDetail,
		progressDetailOptions,
		fundingScheme,
		currentProgress,
		fundingSchemeOptions,
		napNo,
		customer,
		plafonType,
		limitType,
		orderSource,
		requestedLimit,
		approvedLimit,
		fbdh,
		frm,
		brandDetails,
		estimatedGoliveDate,
		estimatedNtf,
		pipelineGoLiveHistory,
		status,
	} = data?.data ?? {};

	const { form, value, fieldName } = useForm<GoliveForm>();
	const { mutateAsync, isPending } = useMutation({
		mutationFn: submitGolive,
	});

	const finalizeGoliveMutation = useMutation({
		mutationFn: finalizeGolive,
	});

	const isMasked = groupName !== 'FRM' && status === 'DRAFTED';

	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<BrandModal {...brandModal} />
			<BrandDetailModal {...brandDetailModal} />
			<div
				style={{
					display: 'flex',
					gap: 10,
					marginBottom: 30,
				}}
			>
				<div>
					<button
						style={{
							display: 'flex',
							height: 44,
							width: 44,
							fontSize: 24,
							border: 'none',
							alignItems: 'center',
							justifyContent: 'center',
							background: '#1557BC',
							color: 'white',
							cursor: 'pointer',
						}}
						onClick={() => {
							navigate('GoliveListPage', { defaultParams: backParams });
						}}
					>
						<ArrowLeftOutlined />
					</button>
				</div>
				<div style={{ flex: 1 }}>
					<FleetTitle>Edit Pipeline Golive</FleetTitle>
				</div>
			</div>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				onFinish={async (values) => {
					const res = await mutateAsync({
						action: values.action,
						brandDetails: (
							values.brandDetails as unknown as BrandModalForm[]
						).map((data) => {
							return {
								brandDetailId: data.brandDetailId!,
								brandId: data.brandId.id,
								brandTypeId: data.brandTypeId.id,
								unit: data.unit,
								amount: data.amount,
							};
						}),
						estimatedGoliveDate: values.estimatedGoliveDate,
						estimatedNtf: values.estimatedNtf,
						fundingSchemeId: values.fundingSchemeId,
						progressDetailId: values.progressDetailId,
						prospectId: values.prospectId,
						prospectPlafonId: values.prospectPlafonId,
						isFinal: values.isFinal,
					});

					if (
						values.action === 'Update' &&
						groupName === 'FBDH' &&
						res.data.finalPipelineGoLiveId
					) {
						await modal.success({
							title: 'Update Data Pipeline Golive telah berhasil!',
							subTitle: (
								<Fragment>
									Berikut adalah Pipeline Golive ID debitur Anda:
									<center
										style={{ fontSize: 40, marginTop: 20, color: 'black' }}
									>
										{res.data.finalPipelineGoLiveId}
									</center>
								</Fragment>
							),
							cancelButtonProps: {
								hidden: true,
							},
							okText: 'BACK',
						});
					} else if (values.action === 'Update') {
						await modal.success({
							title: 'Status Pipeline Debitur berhasil diperbaharui',
							subTitle:
								'Silakan periksa kembali untuk memastikan data sudah sesuai.',
							cancelButtonProps: {
								hidden: true,
							},
							okText: 'BACK',
						});

						navigate('GoliveListPage');
					}
				}}
			>
				<Form.Item
					name={fieldName('action')}
					noStyle
				/>
				<Form.Item
					name={fieldName('prospectId')}
					initialValue={prospectId}
					noStyle
				/>
				<Form.Item
					name={fieldName('prospectPlafonId')}
					initialValue={prospectPlafonId}
					noStyle
				/>
				<div className="form-data-row">
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item label="Prospect ID">{prospectId}</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="NAP ID">{napNo}</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item label="Nama Customer">{customer?.value}</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Jenis Plafon">{plafonType?.value}</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item label="Tipe Limit">{limitType?.value}</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Sumber Order">{orderSource?.value}</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item label="Limit Diajukan">
								{requestedLimit ? formatCurrency(requestedLimit) : '-'}
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Limit Disetujui">
								{approvedLimit ? formatCurrency(approvedLimit) : '-'}
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item label="FBDH">{fbdh?.value}</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="FRM">{frm?.value}</Form.Item>
						</Col>
					</Row>
					<Divider />
				</div>
				<Row
					gutter={40}
					style={{ marginTop: 20 }}
				>
					<Col span={12}>
						<Form.Item label="Progress Saat Ini">{currentProgress}</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Detail Progress"
							name={fieldName('progressDetailId')}
							initialValue={isMasked ? null : progressDetail?.id}
							rules={
								currentProgress === 'Approval Plafon' ? [rule.required()] : []
							}
						>
							{isMasked ? (
								<Select
									disabled
									options={[]}
								/>
							) : (
								<Select
									allowClear
									options={(progressDetailOptions ?? []).map((data) => {
										return {
											value: data.id,
											label: data.value,
										};
									})}
								/>
							)}
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item
							label="Estimasi Tanggal Golive"
							name={fieldName('estimatedGoliveDate')}
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) =>
								value && dayjs(value).utc(true).toISOString()
							}
							rules={[rule.required()]}
							initialValue={isMasked ? null : estimatedGoliveDate}
						>
							{isMasked ? <DatePicker disabled /> : <DatePicker />}
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Estimasi NTF"
							name={fieldName('estimatedNtf')}
							rules={[rule.required()]}
							initialValue={isMasked ? null : estimatedNtf}
						>
							{isMasked ? (
								<InputNumber disabled />
							) : (
								<InputNumber {...inputCurrency} />
							)}
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Form.Item
					label="Asset Type"
					labelCol={{ span: 6 }}
					wrapperCol={{
						span: 18,
						style: { marginInline: -10 },
					}}
				>
					<Checkbox.Group defaultValue={(assetTypes ?? []).map((d) => d.id)}>
						<Row gutter={[20, 10]}>
							{(assetTypeOptions ?? []).map((data, index) => (
								<Col key={index}>
									<Checkbox
										value={data.id}
										disabled
									>
										<span style={{ color: 'black' }}>{data.value}</span>
									</Checkbox>
								</Col>
							))}
						</Row>
					</Checkbox.Group>
				</Form.Item>
				<Divider />
				<Typography.Title level={3}>Brand & Detail Jenis Unit</Typography.Title>
				<Row style={{ display: 'flex', justifyContent: 'end' }}>
					{isMasked ? (
						<Button
							disabled
							icon={'+'}
							style={{
								color: '#BABABA',
								borderRadius: 50,
								fontSize: 12,
								height: 24,
								borderColor: '#BABABA',
								fontWeight: 700,
								marginBottom: 20,
							}}
						>
							Add Another
						</Button>
					) : (
						<Button
							style={{
								color: '#1557BC',
								borderRadius: 50,
								fontSize: 12,
								height: 24,
								borderColor: '#1557BC',
								fontWeight: 700,
								marginBottom: 20,
							}}
							icon={'+'}
							onClick={async () => {
								const newData = await brandModal.open();
								if (!newData) return;

								form.setFieldValue(fieldName('brandDetails'), [
									...(value?.brandDetails ?? []),
									newData,
								]);
							}}
						>
							Add Another
						</Button>
					)}
				</Row>
				<Form.Item
					name={fieldName('brandDetails')}
					initialValue={
						(brandDetails ?? []).map((data) => {
							return {
								amount: data.amount,
								brandId: {
									id: data.brandId,
									value: data.brandName,
								},
								brandTypeId: {
									id: data.brandTypeId,
									value: data.brandTypeName,
								},
								unit: data.unit,
								brandDetailId: data.brandDetailId,
							};
						}) as BrandModalForm[]
					}
					noStyle
				/>
				<Table
					bordered
					pagination={false}
					style={{ marginBottom: 20 }}
					dataSource={(status === 'DRAFTED' && groupName !== 'FRM'
						? []
						: (value?.brandDetails as unknown as BrandModalForm[]) ?? []
					).map((data, i) => {
						return {
							...data,
							key: i + 1,
						};
					})}
					columns={[
						{
							title: 'Action',
							width: 100,
							align: 'center',
							render: (_, record, index) => (
								<a
									onClick={async (e) => {
										e.preventDefault();
										const editData = await brandModal.open(record);
										if (!editData) return;

										form.setFieldValue(
											fieldName('brandDetails'),
											value?.brandDetails?.map((data, dataIndex) => {
												return index === dataIndex ? editData : data;
											}),
										);
									}}
								>
									<EditOutlined />
								</a>
							),
						},
						{
							width: 100,
							align: 'center',
							render: (_, __, index) => (
								<a
									onClick={(e) => {
										e.preventDefault();
										form.setFieldValue(
											fieldName('brandDetails'),
											value?.brandDetails?.filter((_, i) => index !== i),
										);
									}}
								>
									<DeleteOutlined />
								</a>
							),
						},
						{
							title: 'Brand',
							render: (_, record) => record.brandId?.value,
						},
						{
							title: 'Type',
							render: (_, record) => record.brandTypeId?.value,
						},
						{
							title: 'Jumlah Unit',
							render: (_, record) => record.unit,
						},
						{
							title: 'Amount',
							render: (_, record) =>
								record.amount ? formatCurrency(record.amount) : '-',
						},
					]}
				/>

				<Row gutter={40}>
					<Col span={12}>
						<Form.Item
							label="Skema Pembiayaan"
							name={fieldName('fundingSchemeId')}
							initialValue={
								groupName !== 'FRM' && status === 'DRAFTED'
									? null
									: fundingScheme?.id
							}
							rules={[rule.required()]}
						>
							{isMasked ? (
								<Select disabled />
							) : (
								<Select
									options={(fundingSchemeOptions ?? []).map((data) => {
										return {
											value: data.id,
											label: data.value,
										};
									})}
								/>
							)}
						</Form.Item>
					</Col>
				</Row>

				{groupName === 'FBDH' && (
					<Form.Item
						name={fieldName('isFinal')}
						valuePropName="checked"
						wrapperCol={{ span: 24 }}
						style={{ marginTop: 20 }}
					>
						{status === 'DRAFTED' ? (
							<Checkbox disabled>
								<div style={{ fontSize: 14, paddingLeft: 10 }}>
									Mohon checklist kotak di samping jika hasil{' '}
									<strong style={{ color: '#1557BC' }}>Pipeline Golive</strong>{' '}
									sudah final.
								</div>
							</Checkbox>
						) : (
							<Checkbox>
								<div style={{ fontSize: 14, paddingLeft: 10 }}>
									Mohon checklist kotak di samping jika hasil{' '}
									<strong style={{ color: '#1557BC' }}>Pipeline Golive</strong>{' '}
									sudah final.
								</div>
							</Checkbox>
						)}
					</Form.Item>
				)}
				<Space
					style={{ display: 'flex', justifyContent: 'end', marginTop: 100 }}
					size="middle"
				>
					{groupName === 'FRM' && (
						<Button
							style={{
								width: 200,
								display: 'block',
							}}
							disabled={isPending || status === 'UPDATED'}
							loading={isPending && value?.action === 'Draft'}
							onClick={() => {
								form.setFieldValue(fieldName('action'), 'Draft');
								form.submit();
							}}
						>
							SAVE DRAFT
						</Button>
					)}
					{isMasked ? (
						<Button
							disabled
							type="primary"
							style={{
								width: 200,
								display: 'block',
							}}
						>
							UPDATE
						</Button>
					) : (
						<Button
							type="primary"
							style={{
								width: 200,
								display: 'block',
							}}
							disabled={isPending}
							loading={isPending && value?.action === 'Update'}
							onClick={async () => {
								form.setFieldValue(fieldName('action'), 'Update');
								const isConfirmed = await modal.confirm({
									title:
										'Apakah Anda yakin ingin mengubah status pipeline debitur?',
									cancelText: 'TIDAK',
									okText: 'YA',
								});

								if (!isConfirmed) return;
								form.submit();
							}}
						>
							UPDATE
						</Button>
					)}
				</Space>
			</Form>
			<Divider />
			<FleetTitle>Historical Pipeline Golive</FleetTitle>
			<Table
				bordered
				scroll={{ x: 'max-content' }}
				pagination={false}
				style={{ marginTop: 20 }}
				dataSource={
					(pipelineGoLiveHistory?.items.length
						? [
								...pipelineGoLiveHistory.items,
								{
									key: 'total',
								} as unknown as (typeof pipelineGoLiveHistory.items)[number],
							]
						: []
					)?.map((data, i) => {
						return {
							key: i + 1,
							...data,
						};
					}) ?? []
				}
				rowHoverable={false}
				onRow={(record) => {
					if (typeof record.key === 'string') {
						return {
							style: {
								background: '#d0dfff',
							},
						};
					}
					return {};
				}}
				columns={[
					{
						title: 'Prospect ID',
						render: (_, record) => record.prospectId,
					},
					{
						title: 'NAP ID',
						render: (_, record) => record.napNo,
					},
					{
						title: 'Nama Customer',
						render: (_, record) => record.customer?.value,
					},
					{
						title: 'Jenis Plafon',
						render: (_, record) => record.plafonType,
					},
					{
						title: 'Tipe Limit',
						render: (_, record) => record.limitType,
					},
					{
						title: 'Sumber Order',
						render: (_, record) => record.orderSource,
					},
					{
						title: 'FBDH',
						render: (_, record) => record.fbdhName,
					},
					{
						title: 'FRM',
						render: (_, record) => record.frmName,
					},
					{
						title: 'Progress Saat Ini',
						render: (_, record) => record.currentProgress,
					},
					{
						title: 'Estimasi Tanggal Golive',
						render: (_, record) =>
							record.estimatedGoliveDate &&
							dayjs(record.estimatedGoliveDate).format('DD/MM/YYYY'),
					},
					{
						title: 'Asset Type',
						render: (_, record) => record.assetType,
					},
					{
						title: 'Brand & Detail Jenis Unit',
						align: 'center',
						render: (_, record) => {
							if (typeof record.key !== 'number') return null;
							if (!record.pipelineGoLiveId) {
								return <FileSearchOutlined style={{ color: '#bababa' }} />;
							}

							return (
								<a
									onClick={async (e) => {
										e.preventDefault();
										if (getGoliveBrandMutation.isPending) return;

										const res = await getGoliveBrandMutation.mutateAsync(
											record.pipelineGoLiveId,
										);

										if (!res.data.length) {
											return notification.warning({
												message: 'Tidak ada brand & jenis unit tersimpan',
											});
										}

										brandDetailModal.open(res.data);
									}}
								>
									{getGoliveBrandMutation.isPending &&
									getGoliveBrandMutation.variables ===
										record.pipelineGoLiveId ? (
										<LoadingOutlined />
									) : (
										<FileSearchOutlined />
									)}
								</a>
							);
						},
					},
					{
						title: 'Skema Pembiayaan',
						render: (_, record) => record.fundingScheme,
					},
					{
						title: 'Limit Diajukan',
						render: (_, record) =>
							typeof record.key === 'number' &&
							formatCurrency(record.requestedLimit),
					},
					{
						title: 'Limit Disetujui',
						render: (_, record) =>
							typeof record.key === 'number' ? (
								formatCurrency(record.approvedLimit)
							) : (
								<b>Total</b>
							),
					},
					{
						title: 'Estimasi NTF',
						render: (_, record) =>
							typeof record.key === 'number' ? (
								formatCurrency(record.estimatedNtf)
							) : (
								<b>
									{formatCurrency(
										pipelineGoLiveHistory?.totalEstimatedNtf ?? 0,
									)}
								</b>
							),
					},
					{
						title: 'Jumlah Unit',
						render: (_, record) =>
							typeof record.key === 'number' ? (
								formatCurrency(record.units)
							) : (
								<b>{formatCurrency(pipelineGoLiveHistory?.totalUnits ?? 0)}</b>
							),
					},
					{
						title: 'Last Modified',
						render: (_, record) =>
							typeof record.key === 'number' && (
								<strong
									style={{
										color: '#BCBCBC',
										fontStyle: 'italic',
									}}
								>
									{`${record.lastModifiedAt ? dayjs(record.lastModifiedAt).format('DD/MM/YYYY, HH:mm') : ''} - ${record.lastModifiedByName ?? ''}`}
								</strong>
							),
					},
					{
						title: 'Status Golive',
						align: 'center',
						fixed: 'right',
						render: (_, record) =>
							typeof record.key === 'number' && (
								<a
									onClick={async (e) => {
										e.preventDefault();
										if (record.status === 'DONE' || groupName !== 'FBDH')
											return;

										const isConfirmed = await modal.confirm({
											title:
												'Apakah Anda yakin ingin mengubah status pipeline debitur?',
											cancelText: 'TIDAK',
											okText: 'YA',
										});

										if (!isConfirmed) return;
										await finalizeGoliveMutation.mutateAsync(
											record.finalPipelineGoLiveId,
										);
										refetch();
									}}
								>
									<svg
										width="20"
										height="20"
										viewBox="0 0 64 64"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M32 0C14.32 0 0 14.32 0 32C0 49.68 14.32 64 32 64C49.68 64 64 49.68 64 32C64 14.32 49.68 0 32 0ZM29.54 50.64H24.18L11.22 32.48L16.58 27.48L26.86 37.08L47.42 13.36L52.78 17.12L29.54 50.64Z"
											fill={record.status === 'DONE' ? '#48A11E' : '#BCBCBC'}
										/>
									</svg>
								</a>
							),
					},
				]}
			/>
		</Fragment>
	);
};

export default GolivePage;
